import React, { useState, useEffect } from "react";
import axios from "axios";
import { Container, Form, Button, Card, Row, Col, Modal } from "react-bootstrap";

const Reviews = () => {
    const [reviewData, setReviewData] = useState({
        name: "",
        location: "",
        review: "",
        rating: "",
        image: null,
    });

    const [reviews, setReviews] = useState([]);
    const [message, setMessage] = useState("");
    const [preview, setPreview] = useState(null);
    const [editReviewId, setEditReviewId] = useState(null); // Track the review being edited
    const [showEditModal, setShowEditModal] = useState(false); // Control edit modal visibility

    // Fetch reviews on load
    useEffect(() => {
        fetchReviews();
    }, []);

    const fetchReviews = async () => {
        try {
            const response = await axios.get("https://madhusewingmachines.com/api/reviews");
            setReviews(response.data);
        } catch (error) {
            console.error("Error fetching reviews:", error);
        }
    };

    // Handle form input change
    const handleChange = (e) => {
        if (e.target.name === "image") {
            const file = e.target.files[0];
            setReviewData({ ...reviewData, image: file });
            setPreview(URL.createObjectURL(file)); // Generate image preview
        } else {
            setReviewData({ ...reviewData, [e.target.name]: e.target.value });
        }
    };

    // Handle form submission (for adding or updating a review)
    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage("");

        try {
            const formData = new FormData();
            formData.append("name", reviewData.name);
            formData.append("location", reviewData.location);
            formData.append("review", reviewData.review);
            formData.append("rating", reviewData.rating);
            if (reviewData.image) {
                formData.append("image", reviewData.image);
            }

            if (editReviewId) {
                // Update existing review
                await axios.put(`https://madhusewingmachines.com/api/reviews/${editReviewId}`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                setMessage("Review updated successfully!");
            } else {
                // Add new review
                await axios.post("https://madhusewingmachines.com/api/reviews", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                setMessage("Review added successfully!");
            }

            setReviewData({ name: "", location: "", review: "", rating: "", image: null });
            setPreview(null);
            setEditReviewId(null); // Reset edit mode
            fetchReviews(); // Refresh the list
        } catch (error) {
            setMessage("Failed to submit review. Please try again.");
        }
    };

    // Handle delete review
    const handleDelete = async (id) => {
        try {
            await axios.delete(`https://madhusewingmachines.com/api/reviews/${id}`);
            setMessage("Review deleted successfully!");
            fetchReviews(); // Refresh the list
        } catch (error) {
            setMessage("Failed to delete review. Please try again.");
        }
    };

    // Handle edit review
    const handleEdit = (review) => {
        setReviewData({
            name: review.name,
            location: review.location,
            review: review.review,
            rating: review.rating,
            image: null, // Reset image to avoid overwriting existing image
        });
        setEditReviewId(review._id); // Set the review ID being edited
        setShowEditModal(true); // Open the edit modal
    };

    // Close edit modal and reset form
    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setReviewData({ name: "", location: "", review: "", rating: "", image: null });
        setPreview(null);
        setEditReviewId(null);
    };

    return (
        <Container className="mt-4">
            <Card className="p-4 shadow-lg">
                <h3 className="text-center mb-4">Admin Panel - Add Review</h3>
                {message && <p className="text-center text-success">{message}</p>}

                <Form onSubmit={handleSubmit} encType="multipart/form-data">
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={reviewData.name}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter name"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Location</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="location"
                                    value={reviewData.location}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter location"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label>Review</Form.Label>
                        <Form.Control
                            as="textarea"
                            name="review"
                            value={reviewData.review}
                            onChange={handleChange}
                            required
                            rows={3}
                            placeholder="Write a review..."
                        />
                    </Form.Group>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Rating (1-5)</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="rating"
                                    value={reviewData.rating}
                                    onChange={handleChange}
                                    required
                                    min="1"
                                    max="5"
                                    placeholder="Enter rating"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Upload Image</Form.Label>
                                <Form.Control type="file" name="image" onChange={handleChange} accept="image/*" />
                                {preview && (
                                    <img
                                        src={preview}
                                        alt="Preview"
                                        className="mt-2"
                                        style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "8px" }}
                                    />
                                )}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Button variant="primary" type="submit" className="w-100">
                        {editReviewId ? "Update Review" : "Submit Review"}
                    </Button>
                </Form>
            </Card>

            {/* Display List of Added Reviews */}
            <h4 className="mt-5 text-center">All Reviews</h4>
            {reviews.length > 0 ? (
                <Row className="mt-3">
                    {reviews.map((review) => (
                        <Col md={4} key={review._id}>
                            <Card className="p-3 shadow-sm mb-3">
                                <Card.Body>
                                    <h5 className="fw-bold">{review.name}</h5>
                                    <p className="text-muted">{review.location}</p>
                                    <p className="fst-italic">{review.review}</p>
                                    <p>⭐ {review.rating} / 5</p>
                                    {review.image && (
                                        <img
                                            src={`https://madhusewingmachines.com/uploads/${review.image}`}
                                            alt={review.name}
                                            style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "8px" }}
                                        />
                                    )}
                                    <div className="mt-3 d-flex gap-2">
                                        <Button variant="warning" size="sm" onClick={() => handleEdit(review)}>
                                            Edit
                                        </Button>
                                        <Button variant="danger" size="sm" onClick={() => handleDelete(review._id)}>
                                            Delete
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            ) : (
                <p className="text-center text-muted">No reviews added yet.</p>
            )}

            {/* Edit Modal */}
            <Modal show={showEditModal} onHide={handleCloseEditModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Review</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit} encType="multipart/form-data">
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={reviewData.name}
                                onChange={handleChange}
                                required
                                placeholder="Enter name"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Location</Form.Label>
                            <Form.Control
                                type="text"
                                name="location"
                                value={reviewData.location}
                                onChange={handleChange}
                                required
                                placeholder="Enter location"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Review</Form.Label>
                            <Form.Control
                                as="textarea"
                                name="review"
                                value={reviewData.review}
                                onChange={handleChange}
                                required
                                rows={3}
                                placeholder="Write a review..."
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Rating (1-5)</Form.Label>
                            <Form.Control
                                type="number"
                                name="rating"
                                value={reviewData.rating}
                                onChange={handleChange}
                                required
                                min="1"
                                max="5"
                                placeholder="Enter rating"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Upload Image</Form.Label>
                            <Form.Control type="file" name="image" onChange={handleChange} accept="image/*" />
                            {preview && (
                                <img
                                    src={preview}
                                    alt="Preview"
                                    className="mt-2"
                                    style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "8px" }}
                                />
                            )}
                        </Form.Group>

                        <Button variant="primary" type="submit" className="w-100">
                            Update Review
                        </Button>
                    </Form>
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default Reviews;